import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, Zap, Shield, Clock, Monitor, Watch, MessageSquare, BarChart3, ChevronRight, ArrowRight, Star, Gift, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BetaPopup } from '../components/BetaPopup';

const API = process.env.REACT_APP_BACKEND_URL;

function PricingCard({ tier, name, monthlyPrice, yearlyPrice, yearlySavings, operatories, features, trialDays, popular, billingCycle, onSelect, loading }) {
  const isEnterprise = monthlyPrice === null;
  const price = billingCycle === 'yearly' ? yearlyPrice : monthlyPrice;
  const period = billingCycle === 'yearly' ? 'year' : 'month';

  return (
    <div
      data-testid={`pricing-card-${tier}`}
      className={`relative flex flex-col rounded-2xl border p-8 ${
        popular
          ? 'border-dp-500 bg-slate-800/80 shadow-xl shadow-dp-500/10 ring-1 ring-dp-500/20'
          : 'border-slate-700 bg-slate-800/50'
      }`}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-dp-500 to-dp-600 px-4 py-1 text-xs font-bold text-white tracking-wider uppercase">
          Most Popular
        </div>
      )}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-white mb-2">{name}</h3>
        <p className="text-sm text-slate-400">{operatories} operatories</p>
      </div>
      <div className="mb-8">
        {isEnterprise ? (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">Custom</span>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-white">${price}</span>
              <span className="text-slate-400">/{period}</span>
            </div>
            {billingCycle === 'yearly' && yearlySavings > 0 && (
              <p className="mt-1 text-sm text-green-400 font-medium">
                Save ${yearlySavings.toFixed(0)} vs monthly
              </p>
            )}
            {billingCycle === 'yearly' && !isEnterprise && (
              <p className="mt-1 text-xs text-amber-400/80">Non-refundable annual commitment</p>
            )}
          </>
        )}
        {trialDays > 0 && (
          <p className="mt-2 text-sm text-dp-300 font-medium">
            {trialDays}-day free trial included
          </p>
        )}
      </div>
      <ul className="mb-8 flex-1 space-y-3">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
            <Check className="w-4 h-4 text-dp-300 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        data-testid={`select-plan-${tier}`}
        onClick={() => onSelect(tier)}
        disabled={loading}
        className={`w-full py-6 text-base font-semibold rounded-xl transition-all ${
          popular
            ? 'bg-gradient-to-r from-dp-500 to-dp-600 hover:from-dp-400 hover:to-dp-500 text-white shadow-lg shadow-dp-500/25'
            : isEnterprise
            ? 'bg-slate-700 hover:bg-slate-600 text-white'
            : 'bg-slate-700 hover:bg-slate-600 text-white'
        }`}
      >
        {isEnterprise ? 'Contact Sales' : 'Free Trials Starting Soon'}
        {!isEnterprise && !loading && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>
    </div>
  );
}

function FeatureBlock({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
      <div className="shrink-0 w-12 h-12 rounded-lg bg-dp-500/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-dp-300" />
      </div>
      <div>
        <h4 className="font-semibold text-white mb-1">{title}</h4>
        <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [form, setForm] = useState({ practice_name: '', admin_name: '', admin_email: '', referral_code: '' });
  const [referralValid, setReferralValid] = useState(null);
  const [checkingReferral, setCheckingReferral] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const handleSelectPlan = (tier) => {
    if (tier === 'enterprise') {
      window.location.href = 'mailto:sales@dentalpulse.com?subject=Enterprise%20Inquiry';
      return;
    }
    setSelectedTier(tier);
    setShowTrialForm(true);
  };

  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) { setReferralValid(null); return; }
    setCheckingReferral(true);
    try {
      const res = await axios.get(`${API}/api/referral/validate/${code}`);
      setReferralValid(res.data);
    } catch {
      setReferralValid({ valid: false });
    }
    setCheckingReferral(false);
  };

  const handleStartTrial = async (e) => {
    e.preventDefault();
    if (!form.practice_name || !form.admin_email || !form.admin_name) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const suffix = billingCycle === 'yearly' ? '_yearly' : '_monthly';
      const packageId = selectedTier + suffix;
      const res = await axios.post(`${API}/api/subscribe`, {
        package_id: packageId,
        origin_url: window.location.origin,
        practice_name: form.practice_name,
        admin_email: form.admin_email,
        admin_name: form.admin_name,
        referral_code: form.referral_code || null,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
      setLoading(false);
    }
  };

  const tiers = pricing?.tiers || {};

  const getPrice = (tier) => {
    if (billingCycle === 'yearly') return tiers[tier]?.yearly_price;
    return tiers[tier]?.monthly_price;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <BetaPopup />
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png" 
              alt="DentalPulse" 
              className="h-10 w-auto object-contain"
              data-testid="nav-logo"
            />
          </div>
          <div className="flex items-center gap-4">
            <a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</a>
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
            <Button
              data-testid="nav-login-btn"
              onClick={() => navigate('/login')}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              Sign In
            </Button>
            <Button
              data-testid="nav-trial-btn"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-dp-500 hover:bg-dp-400 text-white"
            >
              Free Trials Starting Soon
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-dp-500/10 border border-dp-500/20 text-dp-300 text-sm font-medium mb-8">
            <Star className="w-4 h-4" />
            Built for dental offices that value time
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
            Keep your office
            <br />
            <span className="bg-gradient-to-r from-dp-300 to-dp-500 bg-clip-text text-transparent">
              on schedule
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Real-time operatory dashboards, instant alerts, and smart timers that keep your entire team in sync. Replace outdated communication systems with DentalPulse.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              data-testid="hero-trial-btn"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="h-12 px-8 bg-gradient-to-r from-dp-500 to-dp-600 hover:from-dp-400 hover:to-dp-500 text-white text-base font-semibold rounded-xl shadow-lg shadow-dp-500/25"
            >
              Free Trials Starting Soon <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <Button
              data-testid="hero-download-btn"
              onClick={() => navigate('/download')}
              variant="outline"
              className="h-12 px-8 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl text-base"
            >
              Already subscribed? Download
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Everything your practice needs</h2>
          <p className="text-slate-400 text-center mb-12 max-w-xl mx-auto">
            Built specifically for dental offices to eliminate communication delays and keep patients moving.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <FeatureBlock icon={Monitor} title="Real-Time Dashboard" desc="See every operatory at a glance with live status, timers, and patient flow tracking." />
            <FeatureBlock icon={Zap} title="Instant Alerts" desc="Dr. Needed, Help Needed, and custom alerts with escalation timers and read receipts." />
            <FeatureBlock icon={Clock} title="Smart Timers" desc="Count-up timers with warning and critical thresholds keep everyone accountable." />
            <FeatureBlock icon={MessageSquare} title="Team Messaging" desc="Direct and group messaging between team members, right inside the app." />
            <FeatureBlock icon={BarChart3} title="Practice Reports" desc="Time averages by provider and practice-wide, so you can identify bottlenecks." />
            <FeatureBlock icon={Watch} title="Smartwatch Alerts" desc="Get alerts on your Apple Watch so providers never miss a notification. $6.99/mo per device." />
            <FeatureBlock icon={Shield} title="Role-Based Access" desc="Admins, doctors, assistants, and front office each see exactly what they need." />
            <FeatureBlock icon={Monitor} title="Desktop App" desc="Native Windows and macOS app with auto-updates. No browser required." />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Simple, transparent pricing</h2>
          <p className="text-slate-400 text-center mb-6">
            Every plan includes a <span className="text-dp-300 font-semibold">7-day free trial</span>. Card required to start.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}>
              Monthly
            </span>
            <button
              data-testid="billing-toggle"
              onClick={() => setBillingCycle(b => b === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-dp-500' : 'bg-slate-700'
              }`}
            >
              <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-0.5'
              }`} />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-slate-500'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <span className="ml-2 px-2.5 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-bold">
                Save up to $100
              </span>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {tiers.starter && (
              <PricingCard
                tier="starter"
                name={tiers.starter.name}
                monthlyPrice={tiers.starter.monthly_price}
                yearlyPrice={tiers.starter.yearly_price}
                yearlySavings={tiers.starter.yearly_savings}
                operatories={tiers.starter.operatories}
                features={tiers.starter.features}
                trialDays={tiers.starter.trial_days}
                billingCycle={billingCycle}
                onSelect={handleSelectPlan}
                loading={loading && selectedTier === 'starter'}
              />
            )}
            {tiers.professional && (
              <PricingCard
                tier="professional"
                popular
                name={tiers.professional.name}
                monthlyPrice={tiers.professional.monthly_price}
                yearlyPrice={tiers.professional.yearly_price}
                yearlySavings={tiers.professional.yearly_savings}
                operatories={tiers.professional.operatories}
                features={tiers.professional.features}
                trialDays={tiers.professional.trial_days}
                billingCycle={billingCycle}
                onSelect={handleSelectPlan}
                loading={loading && selectedTier === 'professional'}
              />
            )}
            {tiers.enterprise && (
              <PricingCard
                tier="enterprise"
                name={tiers.enterprise.name}
                monthlyPrice={tiers.enterprise.monthly_price}
                yearlyPrice={tiers.enterprise.yearly_price}
                yearlySavings={0}
                operatories={tiers.enterprise.operatories}
                features={tiers.enterprise.features}
                trialDays={tiers.enterprise.trial_days}
                billingCycle={billingCycle}
                onSelect={handleSelectPlan}
                loading={false}
              />
            )}
          </div>

          {/* Smartwatch add-on */}
          <div className="mt-12 text-center p-6 rounded-xl border border-slate-700/50 bg-slate-800/30 max-w-xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Watch className="w-5 h-5 text-dp-300" />
              <span className="font-semibold text-white">Smartwatch Integration</span>
            </div>
            <p className="text-slate-400 text-sm">
              Add Apple Watch alerts for <span className="text-white font-semibold">$6.99/month per device</span>. Available as an add-on to any plan.
            </p>
          </div>
        </div>
      </section>

      {/* Referral CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-8 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50">
            <Gift className="w-10 h-10 text-dp-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Refer a Practice, Earn a Free Month</h3>
            <p className="text-slate-400 max-w-lg mx-auto mb-2">
              Know another dental office that could use DentalPulse? Share your referral code and earn <span className="text-white font-semibold">1 month free</span> when they subscribe.
            </p>
            <p className="text-slate-500 text-sm">
              Yearly subscribers receive a prorated credit (1/12 of annual cost) applied at renewal.
            </p>
          </div>
        </div>
      </section>

      {/* Trial signup modal */}
      {showTrialForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-2">
              Start your 7-day free trial
            </h3>
            <p className="text-sm text-slate-400 mb-1">
              {selectedTier === 'professional' ? 'Professional' : 'Starter'} plan
              {billingCycle === 'yearly'
                ? ` — $${getPrice(selectedTier)}/yr after trial`
                : ` — $${getPrice(selectedTier)}/mo after trial`}
            </p>
            {billingCycle === 'yearly' && (
              <p className="text-xs text-amber-400/80 mb-4">Annual billing is non-refundable</p>
            )}
            {billingCycle !== 'yearly' && <div className="mb-4" />}
            <form onSubmit={handleStartTrial} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Practice Name</label>
                <input
                  data-testid="trial-practice-name"
                  type="text"
                  value={form.practice_name}
                  onChange={e => setForm(f => ({ ...f, practice_name: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-dp-500 focus:outline-none"
                  placeholder="Bright Smiles Dental"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Your Name</label>
                <input
                  data-testid="trial-admin-name"
                  type="text"
                  value={form.admin_name}
                  onChange={e => setForm(f => ({ ...f, admin_name: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-dp-500 focus:outline-none"
                  placeholder="Dr. Smith"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Email</label>
                <input
                  data-testid="trial-admin-email"
                  type="email"
                  value={form.admin_email}
                  onChange={e => setForm(f => ({ ...f, admin_email: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-dp-500 focus:outline-none"
                  placeholder="dr.smith@practice.com"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  Referral Code <span className="text-slate-500">(optional)</span>
                </label>
                <div className="relative">
                  <input
                    data-testid="trial-referral-code"
                    type="text"
                    value={form.referral_code}
                    onChange={e => {
                      const val = e.target.value.toUpperCase();
                      setForm(f => ({ ...f, referral_code: val }));
                      if (val.length >= 5) validateReferralCode(val);
                      else setReferralValid(null);
                    }}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-dp-500 focus:outline-none uppercase"
                    placeholder="DP-XXXXXX"
                  />
                  {checkingReferral && (
                    <Loader2 className="absolute right-3 top-3 w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>
                {referralValid?.valid && (
                  <p className="mt-1 text-xs text-green-400 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Referred by {referralValid.referred_by}
                  </p>
                )}
                {referralValid && !referralValid.valid && (
                  <p className="mt-1 text-xs text-red-400">Invalid referral code</p>
                )}
              </div>
              <Button
                data-testid="trial-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-5 bg-gradient-to-r from-dp-500 to-dp-600 hover:from-dp-400 hover:to-dp-500 text-white font-semibold rounded-xl"
              >
                {loading ? 'Redirecting to checkout...' : 'Continue to Payment'}
              </Button>
              <button
                type="button"
                onClick={() => { setShowTrialForm(false); setLoading(false); setReferralValid(null); }}
                className="w-full text-sm text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-800 py-10 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png" 
              alt="DentalPulse" 
              className="h-7 w-auto object-contain"
              data-testid="footer-logo"
            />
          </div>
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} DentalPulse. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
